import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import AuthService from "../../common/AuthService";
import {
  getLicences,
  IGetLicencesResponceModel,
} from "../../webapi/service/getLicences";
import { registCode } from "../../webapi/service/registCode";
import { registTerm } from "../../webapi/service/registTerm";
import PromptDialog, { InputTextItem } from "../../common/PromptDialog";
import { IConfirmDialogParams } from "../../common/ConfirmDialog";
import "../main.css";
import "./license.css";
import { registMacaddr } from "../../webapi/service/registMacaddress";
import { updateMacaddr } from "../../webapi/service/updateMacaddress";
import MacaddrList, { MaxMemoLength } from "./MacaddrList";

function createList(title: string, array: string[]) {
  const tr: JSX.Element[] = [];
  array.forEach((str, i) => {
    tr.push(
      <tr key={i}>
        <td>{str}</td>
      </tr>
    );
  });
  return (
    <table>
      <thead>
        <tr>
          <th>{title}</th>
        </tr>
      </thead>
      <tbody>{tr}</tbody>
    </table>
  );
}

export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (params: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットします
   */
  resetUnoperatedTimeout: () => void;
}

interface IState extends IGetLicencesResponceModel {
  /**
   * MAC Address登録ダイアログを表示する場合はtrue
   */
  showMacaddr: boolean;
  /**
   * ライセンス登録ダイアログを表示する場合はtrue
   */
  showLicense: boolean;
  /**
   * ライセンス登録ダイアログ表示モード
   * REGIST_MODEL_LICENSE | REGIST_TERM_LICENSE
   */
  registLicense: number;
  /**
   * MACアドレス登録ダイアログMACアドレス初期表示
   */
  macaddrText: string;
  /**
   * MACアドレス登録ダイアログメモ初期表示
   */
  macaddrMemo: string;
}

class LicenseMainSpace extends React.Component<IProps, IState> {
  private readonly REGIST_MODEL_LICENSE = 0;
  private readonly REGIST_TERM_LICENSE = 1;

  constructor(props: IProps) {
    super(props);

    this.state = {
      max: 0,
      registerd: 0,
      codes: [],
      macaddrs: [],
      termcode: "",
      termexpiration: "",
      showMacaddr: false,
      showLicense: false,
      registLicense: this.REGIST_MODEL_LICENSE,
      macaddrText: "",
      macaddrMemo: "",
    };

    this.handleShowLicenseDialog = this.handleShowLicenseDialog.bind(this);
    this.handleShowMacaddrDialog = this.handleShowMacaddrDialog.bind(this);
    this.handleHideMacaddrDialog = this.handleHideMacaddrDialog.bind(this);
    this.handleRegistMacaddr = this.handleRegistMacaddr.bind(this);
    this.handleUpdateMacaddr = this.handleUpdateMacaddr.bind(this);
    this.handleRegistLicense = this.handleRegistLicense.bind(this);
    this.handleRegistTermLicense = this.handleRegistTermLicense.bind(this);
  }

  componentDidMount() {
    this.loadLicense(true);
  }

  loadLicense(loading: boolean) {
    const { authService, onLoading, onCommunicationError } = this.props;
    onLoading(loading);
    getLicences({
      authService: authService,
      success: (max, registerd, codes, macaddrs, termcode, termexpiration) => {
        this.setState({
          max: max,
          registerd: registerd,
          codes: codes,
          macaddrs: macaddrs,
          termcode: termcode,
          termexpiration: termexpiration,
        });
        onLoading(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleShowMacaddrDialog() {
    this.props.resetUnoperatedTimeout();
    this.setState({ showMacaddr: true, macaddrText: "", macaddrMemo: "" });
  }

  handleHideMacaddrDialog() {
    this.setState({ showMacaddr: false, macaddrText: "", macaddrMemo: "" });
  }

  handleRegistMacaddr(texts: InputTextItem[]) {
    const macaddr = texts[0].text;
    const memo = texts[1].text;
    const { showConfirm, t } = this.props;
    this.setState({ showMacaddr: false, macaddrText: macaddr, macaddrMemo: memo });
    showConfirm({
      title: t("license_macaddr_register_confirm"),
      message: t("license_macaddr_register_confirm_message") + "\n" + macaddr,
      onOk: () => {
        const {
          authService,
          onLoading,
          onCommunicationError,
          resetUnoperatedTimeout,
        } = this.props;
        onLoading(true);
        resetUnoperatedTimeout();
        registMacaddr({
          authService,
          macaddr: macaddr,
          memo: memo,
          success: () => {
            this.loadLicense(false);
          },
          fail: (id, status) => {
            onCommunicationError(id, status);
          },
        });
      },
      onCancel: () => {
        this.setState({ showMacaddr: true });
      },
    });
  }

  handleUpdateMacaddr(macaddr: string, memo: string) {
    const {
      authService,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    onLoading(true);
    resetUnoperatedTimeout();
    updateMacaddr({
      authService: authService,
      macaddr: macaddr,
      memo: memo,
      success: () => {
        this.loadLicense(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleShowLicenseDialog(show: boolean, registLicense?: number) {
    if (this.state.showLicense !== show) {
      this.props.resetUnoperatedTimeout();
      if (typeof registLicense !== "undefined") {
        this.setState({ showLicense: show, registLicense: registLicense });
      } else {
        this.setState({ showLicense: show });
      }
    }
  }

  handleRegistLicense(texts: InputTextItem[]) {
    const { authService, onLoading, onCommunicationError } = this.props;
    const { text } = texts[0];
    onLoading(true);
    this.handleShowLicenseDialog(false);
    registCode({
      authService,
      code: text,
      success: () => {
        this.loadLicense(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleRegistTermLicense(texts: InputTextItem[]) {
    const { authService, onLoading, onCommunicationError } = this.props;
    const { text } = texts[0];
    onLoading(true);
    this.handleShowLicenseDialog(false);
    registTerm({
      authService,
      termcode: text,
      success: () => {
        this.loadLicense(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  render() {
    const { t } = this.props;
    const {
      max,
      registerd,
      codes,
      macaddrs,
      termcode,
      termexpiration,
      showMacaddr,
      showLicense,
      registLicense,
      macaddrText,
      macaddrMemo,
    } = this.state;
    const macaddrBtn =
      codes.length > 0 && max - registerd > 0 ? (
        <Button className="btn-mainspace" onClick={this.handleShowMacaddrDialog}>
          {t("license_macaddr_register")}
        </Button>
      ) : (
        ""
      );
    const codeList = createList(t("license_license_list"), codes);
    const termexpirationList = createList(t("license_termexpiration"), [
      termexpiration,
    ]);
    const termcodeList = createList(t("license_termcode"), [termcode]);
    const ok =
      registLicense === this.REGIST_MODEL_LICENSE
        ? this.handleRegistLicense
        : this.handleRegistTermLicense;
    const downloadLicense = (
      <React.Fragment>
        <div className="mainspace-title">
          <h3>{t("license_download")}</h3>
        </div>
        <div className="mainspace-toolbar">
          {macaddrBtn}
          <Button
            className="btn-mainspace"
            onClick={() =>
              this.handleShowLicenseDialog(true, this.REGIST_MODEL_LICENSE)
            }
          >
            {t("license_license_register")}
          </Button>
        </div>
        <div className="license-count">
          <div className="item">
            <div className="title">{t("license_macaddr_all")} :</div>
            <div>{max}</div>
          </div>
          <div className="item">
            <div className="title">{t("license_macaddr_left")} :</div>
            <div>{max - registerd}</div>
          </div>
        </div>
        <div className="license-list">
          <div className="serial-item">
            <MacaddrList 
              macaddrs={macaddrs}
              onUpdateMacaddr={this.handleUpdateMacaddr}
            />
          </div>
          <div className="code-item">{codeList}</div>
        </div>
      </React.Fragment>
    );
    const cloudLicense = (
      <React.Fragment>
        <div className="mainspace-title">
          <h3>{t("license_cloud")}</h3>
        </div>
        <div className="mainspace-toolbar">
          <Button
            className="btn-mainspace"
            onClick={() =>
              this.handleShowLicenseDialog(true, this.REGIST_TERM_LICENSE)
            }
          >
            {t("license_license_register")}
          </Button>
        </div>
        <div className="cloud-license-list">
          <div className="item">{termexpirationList}</div>
          <div className="item">{termcodeList}</div>
        </div>
      </React.Fragment>
    );
    const contents = (
      <div style={{ overflow: "auto" }}>
        <div className="mainspace">
          {downloadLicense}
          {cloudLicense}
        </div>
        <PromptDialog
          show={showMacaddr}
          title={t("license_macaddr_register")}
          items={[
            {
              id: "macaddr",
              label: t("license_macaddr_register_macaddr"),
              placeholder: t("err_license_macaddr_register"),
              text: macaddrText,
              validate: { length: { min: 17, max: 17 } },
              inputValue: (item, value) => {
                const regex = /^[0-9a-fA-F]+$/;
                const replace = value.replace(/:/g, "");               
                if (value.length === 0) {
                  item.text = value;
                } else if (replace.length <= 12 && regex.test(replace)) {                 
                  let t = replace;
                  const idx1 = 2;
                  const idx2 = 5;
                  const idx3 = 8;
                  const idx4 = 11;
                  const idx5 = 14;
                  const compleate = ":";
                  if (t.length > idx1 && t[idx1] !== compleate) {
                    t = t.substring(0, idx1) + compleate + t.substring(idx1);
                  }
                  if (t.length > idx2 && t[idx2] !== compleate) {
                    t = t.substring(0, idx2) + compleate + t.substring(idx2);
                  }
                  if (t.length > idx3 && t[idx3] !== compleate) {
                    t = t.substring(0, idx3) + compleate + t.substring(idx3);
                  }
                  if (t.length > idx4 && t[idx4] !== compleate) {
                    t = t.substring(0, idx4) + compleate + t.substring(idx4);
                  }
                  if (t.length > idx5 && t[idx5] !== compleate) {
                    t = t.substring(0, idx5) + compleate + t.substring(idx5);
                  }
                  item.text = t.toLowerCase();
                }
              },
            },
            {
              id: "memo",
              label: t("license_macaddr_register_memo"),
              text: macaddrMemo,
              maxLength: MaxMemoLength,
            },
          ]}
          onOk={this.handleRegistMacaddr}
          onCancel={this.handleHideMacaddrDialog}
        />
        <PromptDialog
          show={showLicense}
          title={t("license_license_register")}
          items={[
            {
              id: "license",
              label: t("license_license_register_macaddr"),
              placeholder: t("err_license_license_register"),
              validate: { length: { min: 19, max: 19 } },
              inputValue: (item, value) => {
                const regex = /^[0-9a-zA-Z]+$/;
                const replace = value.replace(/-/g, "");
                if (value.length === 0) {
                  item.text = value;
                } else if (replace.length <= 16 && regex.test(replace)) {
                  let t = replace;
                  const idx1 = 3;
                  const idx2 = 8;
                  const idx3 = 14;
                  const compleate = "-";
                  if (t.length > idx1 && t[idx1] !== compleate) {
                    t = t.substring(0, idx1) + compleate + t.substring(idx1);
                  }
                  if (t.length > idx2 && t[idx2] !== compleate) {
                    t = t.substring(0, idx2) + compleate + t.substring(idx2);
                  }
                  if (t.length > idx3 && t[idx3] !== compleate) {
                    t = t.substring(0, idx3) + compleate + t.substring(idx3);
                  }
                  item.text = t.toUpperCase();
                }
              },
            },
          ]}
          onOk={ok}
          onCancel={() => this.handleShowLicenseDialog(false)}
        />
      </div>
    );
    return contents;
  }
}

export default withTranslation()(LicenseMainSpace);
